import { getOwner, onCleanup } from "solid-js"

export type EmitterPayload<M> = { [K in keyof M]: { readonly name: K; readonly details: M[K] } }[keyof M]

/**
 * Named events plus a listener for every event. A subscription made inside a
 * reactive owner ends with it; every subscription ends when the emitter's own
 * owner is disposed. Listeners added during an emit hear it; listeners removed
 * before their turn do not.
 */
export function createGlobalEmitter<M extends Record<PropertyKey, unknown>>() {
  const named = new Map<keyof M, Set<(details: never) => void>>()
  const all = new Set<(payload: EmitterPayload<M>) => void>()
  const subscribe = <L>(set: Set<L>, listener: L, remove: () => void) => {
    set.add(listener)
    if (getOwner()) onCleanup(remove)
    return remove
  }
  onCleanup(() => {
    named.clear()
    all.clear()
  })
  return {
    on<K extends keyof M>(event: K, listener: (details: M[K]) => void) {
      let listeners = named.get(event)
      if (!listeners) named.set(event, (listeners = new Set()))
      return subscribe(listeners, listener, () => {
        const current = named.get(event)
        if (current?.delete(listener) && current.size === 0) named.delete(event)
      })
    },
    listen(listener: (payload: EmitterPayload<M>) => void) {
      return subscribe(all, listener, () => void all.delete(listener))
    },
    emit<K extends keyof M>(name: K, details: M[K]) {
      for (const listener of all) listener({ name, details } as EmitterPayload<M>)
      const listeners = named.get(name)
      if (listeners) for (const listener of listeners) (listener as (details: M[K]) => void)(details)
    },
  }
}
